import { useEffect, useMemo, useState } from "react";
import apiService from "../services/api";
import eventsLocal from "../data/events";
import { mapEventsToActivities } from "../utils/mapEventsToActivities";

/**
 * Hook to fetch and transform events into activities format
 * Similar pattern to useCategorizedEvents but for home page activities
 * Filters events with status.id === 1 and sorts by date
 */
export function useActivityEvents() {
  const [activities, setActivities] = useState(
    mapEventsToActivities(eventsLocal)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchActivities() {
      try {
        setLoading(true);
        setError(null);

        const json = await apiService.getEvents();
        const events = json.data || json;

        if (!Array.isArray(events)) {
          throw new Error("Invalid events data format");
        }

        // Filter events with status.id === 1 and transform to activities format
        const filtered = events.filter((e) => e.status?.id === 1);
        const transformed = mapEventsToActivities(filtered);

        if (isMounted) {
          setActivities(transformed);
        }
      } catch (err) {
        console.warn("Error fetching activities, using fallback:", err.message);
        if (isMounted) {
          setError(err.message);
          // Fallback to local data
          setActivities(mapEventsToActivities(eventsLocal));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchActivities();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, []);

  // Sort activities by date (newest first)
  const sorted = useMemo(() => {
    return [...activities].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Newest first
    });
  }, [activities]);

  return {
    activities: sorted,
    loading,
    error,
  };
}
